import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useAppBasePath } from '../_shared/base-path';
import { useCurrentUser } from '../_shared/hooks';
import { ContactFlowTable, AccountFlowTable, AccountDrillDown, GlobalSearch } from '../_shared/components';
// Imported from the flow barrel directly (not the ../_shared/components index
// barrel above) — that index barrel re-exports AppDirectory, which pulls in
// ../registry's top-level `import.meta.glob`, a Vite-only macro that throws
// under Bun's test runner. See mini-apps/_shared/components/flow.ts's header
// comment.
import { PlaysSubtab, SignalsFlowTable } from '../_shared/components/flow';
import { VISIBLE_SUBTABS, useCommandCenterSubtab, type Subtab } from './subtabs';

/**
 * Thin shell — routing + layout only. The tables, drill-down, omnibox, and
 * all data logic live in core (web/app-home/src/components/flow + /search),
 * imported via _shared. The shell owns the persistent header (title +
 * omnibox + theme toggle) and supplies the omnibox's navigation callbacks.
 */

function CommandCenterHome({ subtab }: { subtab: Subtab }) {
  const navigate = useNavigate();

  if (subtab === 'plays') {
    return <PlaysSubtab />;
  }

  if (subtab === 'signals') {
    return <SignalsFlowTable fillHeight />;
  }

  return subtab === 'contact' ? (
    // Global mount: all owned contacts.
    <ContactFlowTable fillHeight />
  ) : (
    // Ranked account table — rows navigate to the drill-down.
    <AccountFlowTable onAccountClick={id => navigate(`accounts/${id}`)} fillHeight />
  );
}

function AccountDrillDownRoute() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const appBase = useAppBasePath();
  if (!accountId) return null;
  // This route no longer touches `?contact=` — `ContactFlowTable` is its
  // sole reader/writer now, for every mount (global and account-scoped
  // alike). See `ContactFlowTable.tsx`'s `contactQueryParam` doc comment for
  // the unification history; previously this route read the param via nuqs
  // and threaded it down as an `initialContactId`/`initialExpandedId` prop.
  return (
    <AccountDrillDown
      accountId={accountId}
      // "← Accounts" means the accounts LIST, not history-back: a drill-down
      // reached via a cross-app deep-link (Action View "T", omnibox) would
      // otherwise "go back" to the app the rep came FROM (Spencer, 2026-07-24).
      onBack={() => navigate(appBase)}
    />
  );
}

export default function CommandCenterApp() {
  const { isLoading } = useCurrentUser();
  const navigate = useNavigate();
  // Absolute base for omnibox navigation — the shell renders above
  // <Routes>, so relative paths would resolve against whichever child
  // route is active. Provided by the host mount (MiniAppLayout at
  // `/tools/command-center`, the Flow shell at `/flow/command-center`) so this
  // component works dual-mounted without hardcoding either.
  const appBase = useAppBasePath();
  // Lifted so the omnibox knows the active entity (preferred result section)
  // on both routes; on the drill-down it's the last-selected subtab. Shared
  // with shell-next/Header via useCommandCenterSubtab so both shells land on
  // identical URL state by construction.
  const { subtab, onHome, setSubtab: handleSubtabChange, initialQuery } = useCommandCenterSubtab(appBase);

  if (isLoading) {
    return <div className="p-6 text-content-tertiary dark:text-nightshade-500">Loading...</div>;
  }

  const handleSelectAccount = (id: string) => navigate(`${appBase}/accounts/${id}`);
  const handleSelectContact = (contactId: string, accountId: string | null) => {
    if (accountId) {
      navigate(`${appBase}/accounts/${accountId}?contact=${contactId}`);
    } else {
      // Orphan contact (no account in the mirror): can't drill in — land on
      // the global contacts list instead of crashing. Single URL write (not
      // setSubtab + navigate): a queued nuqs write followed immediately by a
      // react-router navigate resets nuqs's pending queue, so a plain
      // setSubtab('contact') here is silently dropped and the rep lands on
      // the default Accounts tab. Mirror handleSubtabChange's off-home
      // branch — carry the tab in the URL directly.
      navigate(`${appBase}?tab=contact`);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Pinned control bar — Flow standard: shell tabs above, per-app
          controls here (Action View's toolbar pattern). App identity lives
          in the accent-colored name, not a hero title. */}
      <div className="sticky top-0 z-40 bg-white dark:bg-nightshade-900 border-b border-nightshade-200 dark:border-nightshade-700">
        <div className="w-full flex h-12 items-center gap-2 px-6">
          <span className="text-[15px] font-bold text-pomegranate-600 dark:text-pomegranate-400">Command Center</span>

          <div className="ml-3 flex gap-1">
            {VISIBLE_SUBTABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                aria-pressed={subtab === key && onHome}
                onClick={() => handleSubtabChange(key)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                  subtab === key && onHome
                    ? 'bg-pomegranate-100 dark:bg-pomegranate-900/40 text-pomegranate-700 dark:text-pomegranate-300'
                    : 'text-nightshade-600 dark:text-nightshade-400 hover:bg-nightshade-100 dark:hover:bg-nightshade-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="ml-auto">
            <GlobalSearch
              // GlobalSearch's activeEntity is typed 'account' | 'contact' —
              // it has no 'plays'/'signals' concept, so both subtabs prefer
              // contact results, same as the drill-down's own subtab.
              activeEntity={subtab === 'account' || subtab === 'contact' ? subtab : 'contact'}
              onSelectAccount={handleSelectAccount}
              onSelectContact={handleSelectContact}
              initialQuery={initialQuery ?? undefined}
            />
          </div>
        </div>
      </div>

      <div className={`p-6 ${onHome ? 'flex flex-1 min-h-0 flex-col' : ''}`}>
        <Routes>
          <Route index element={<CommandCenterHome subtab={subtab} />} />
          <Route path="accounts/:accountId" element={<AccountDrillDownRoute />} />
        </Routes>
      </div>
    </div>
  );
}
